
import React, { useEffect, useRef, useCallback } from "react";
import embed from "vega-embed";

import ButtonSimple from "@/components/ButtonSimple";
import TooltipCustom from "@/components/TooltipCustom";
import { IconAiOutlineDownload } from "@/utils/icons";
import { lineBreakerForChartTitle } from "@/utils";

const ChartGroupedBar = (props: any) => {

  const { item }: any = props;
  const chartData = item?.data;
  // if(item?.data && item?.data?.length > 0){
  //   return <div />;
  // }
  const legendTitle = item?.legend_title || "";
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewRef = useRef<any>(null);

  const chartRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const handleExport = useCallback(() => {
    if (viewRef.current) {
      viewRef.current.toImageURL('png', 2).then((url: string) => {
        const link = document.createElement('a');
        link.download = `${item?.title || 'chart'}.png`;
        link.href = url;
        link.click();
      });
    }
  }, [item?.title]);

  const renderChart = useCallback(() => {

    if (!chartRef.current || !chartData) {
      return;
    }

    const chartWidth = chartRef.current.offsetWidth > 500 ? chartRef.current.offsetWidth : 400;
    // const chartHeight = chartRef.current.offsetWidth;

    const rowHeight = 70;
    const chartHeight = chartData ? new Set(chartData.map((d: any) => d.category)).size * rowHeight : 200;

    const title = item?.title || "";
    
    const formattedTitle = lineBreakerForChartTitle(title, chartWidth);

    const spec: any = {
      "$schema": "https://vega.github.io/schema/vega/v5.json",
      "description": item?.description || "",
      "width": chartWidth,
      "height": chartHeight,
      "autosize": { "type": "fit", "contains": "padding" },
      // "padding": { "top": 30 },
      "padding": { "top": 0, "bottom": 0, "left": 0, "right": 10 },
      // "autosize": "fit-x", // "fit" || "pad" || "fit-x" || "fit-y"
      // "background": "#c4c4c4",
      "title": {
        "text": formattedTitle,
        "fontSize": 12,
        "fontFamily": "Arial",
        "fontWeight": "bold",
        "color": "black",
        "anchor": "middle",
        "lineHeight": 14
      },
      "data": [
        {
          "name": "table",
          "values": chartData
        },
        {
          "name": "filteredData",
          "source": "table",
          "transform": [
            {
              "type": "filter",
              "expr": "datum.value > 0"
            }
          ]
        },
        {
          "name": "processedData",
          "source": "table",
          "transform": [
            {
              "type": "formula",
              "as": "processedValue",
              "expr": "datum.value === '' ? 0 : datum.value"
            },
            {
              "type": "formula",
              "as": "opacity",
              "expr": "(datum.value === '' || datum.value === 0) ? 0 : 1"
            }
          ]
        }
      ],
      "legends": [
        {
          "fill": "color",
          "title": legendTitle || "",
          "orient": "right",
          "titleFontSize": 10,
          "titleFontFamily": "Arial",
          "titleColor": "black",
          "padding": 0,
          "encode": {
            "labels": {
              "update": {
                // label_cosmetic_controls
                "opacity": { "value": 1 },
                "fontSize": { "value": 10 },
                "fontFamily": { "value": "Arial" },
                "fill": { "value": "black" },
              }
            }
          }
        }
      ],

      "scales": [
        {
          "name": "yscale",
          "type": "band",
          "domain": { "data": "table", "field": "category" },
          "range": "height",      // chart height adjusts to #categories
          "padding": 0.2
        },
        {
          "name": "xscale",
          "type": "linear",
          "domain": { "data": "processedData", "field": "processedValue" },
          "range": "width",       // width auto-fits to container
          "round": true,
          "zero": true,
          "nice": true
        },
        {
          "name": "color",
          "type": "ordinal",
          "domain": { "data": "table", "field": "yearVal" },
          "range": { "data": "table", "field": "colorParam" }
        }
      ],

      "axes": [
        { "orient": "left", "scale": "yscale", "tickSize": 0, "labelPadding": 4, "zindex": 1 },
        { "orient": "bottom", "scale": "xscale" }
      ],

      "marks": [
        {
          "type": "group",

          "from": {
            "facet": {
              "data": "processedData",
              "name": "facet",
              "groupby": "category"
            }
          },

          "encode": {
            "enter": {
              "y": { "scale": "yscale", "field": "category", "type": "text" }
            }
          },

          "signals": [
            { "name": "height", "update": "bandwidth('yscale')" }
          ],

          "scales": [
            {
              "name": "pos",
              "type": "band",
              "range": "height",
              "domain": { "data": "facet", "field": "position" }
            }
          ],

          "marks": [
            {
              "name": "bars",
              "from": { "data": "facet" },
              "type": "rect",
              "encode": {
                "enter": {
                  "y": { "scale": "pos", "field": "position" },
                  "height": { "scale": "pos", "band": 1 },
                  "x": { "scale": "xscale", "field": "processedValue" },
                  "x2": { "scale": "xscale", "value": 0 },
                  "fill": { "scale": "color", "field": "yearVal" },
                  "opacity": { "field": "opacity" }
                }
              }
            },
            {
              "type": "text",
              "from": { "data": "bars" },
              "encode": {
                "enter": {
                  "x": { "field": "x2", "offset": 5 },
                  "y": { "field": "y", "offset": { "field": "height", "mult": 0.5 } },
                  "fill": { "value": "black" },
                  "align": { "value": "left" },
                  "baseline": { "value": "middle" },
                  "text": { "field": "datum.processedValue" },
                  "opacity": { "field": "opacity" }
                }
              }
            }
          ]
        }
      ]
    }

    embed(chartRef.current as unknown as HTMLElement, spec, {
      actions: {
        export: true,
        source: false,
        compiled: false,
        editor: false
      }
    }).then((vegaEmbedResult: any) => {
      viewRef.current = vegaEmbedResult.view;
      const menu = chartRef.current?.querySelector(".vega-actions");
      if (menu) {
        const svgButton = menu.querySelector('a[title="Save as SVG"]');
        if (svgButton) {
          svgButton.remove();
        }
      }
    });

  }, [chartData, item, legendTitle]);


  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      renderChart();
    }, 300);
  }, [renderChart]);


  useEffect(() => {

    renderChart();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      viewRef.current = null;
      resizeTimeoutRef.current = null;
      // chartRef.current = null;
    };
  }, [renderChart, handleResize]);

  return (
    <div>
      <TooltipCustom title="Export as PNG">
        <ButtonSimple
          onClick={handleExport}
          className='flex justify-self-end mb-1'
        >
          <IconAiOutlineDownload />
        </ButtonSimple>
      </TooltipCustom>
      <div id="chart-grouped-bar" className="!p-0 !pr-0 !pe-0 " ref={chartRef}></div>
    </div>
  );
}

export default ChartGroupedBar;